import React, { useState } from 'react';
import { useDaggerheartData } from '../../context/DataContext';
import { ChevronDown, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';
import CampaignFrameSection from './sections/CampaignFrameSection';
import ClassSection from './sections/ClassSection';
import AncestrySection from './sections/AncestrySection';
import TraitsSection from './sections/TraitsSection';
import EquipmentSection from './sections/EquipmentSection';
import StartingItemsSection from './sections/StartingItemsSection';
import DomainCardsSection from './sections/DomainCardsSection';
import DetailsSection from './sections/DetailsSection';
import useFrameConfig from '../../hooks/useFrameConfig';
import { FRAME_MECHANIC_TYPES } from '../../config/campaignFrameConfig';

/**
 * LevelOneEditor - Character creation interface for Level 1
 *
 * Handles all 8 creation sections in an accordion-style layout:
 * 1. Campaign Frame
 * 2. Class & Subclass
 * 3. Ancestry & Community
 * 4. Trait Assignment
 * 5. Equipment
 * 6. Starting Items
 * 7. Domain Cards
 * 8. Character Details
 *
 * Mobile: One section expanded at a time
 * Desktop: Multiple sections can be expanded
 *
 * @param {Object} character - Current character state
 * @param {Function} onUpdate - Callback to update character
 * @param {string} initialSection - Optional section key to expand on mount
 */
function LevelOneEditor({ character, onUpdate, onCharacterForge, initialSection }) {
  const allData = useDaggerheartData();

  // Check for equipment override (e.g., Motherboard's Ikonis system)
  const { mechanics } = useFrameConfig(character?.campaignFrame);
  const hasEquipmentOverride = Object.values(mechanics || {}).some(
    config => config.pattern === FRAME_MECHANIC_TYPES.EQUIPMENT_OVERRIDE
  );

  // Determine initial expanded sections based on initialSection prop
  const getInitialExpandedSections = () => {
    if (initialSection) {
      // If initialSection is provided, expand only that section
      return {
        campaign: initialSection === 'campaign',
        class: initialSection === 'class',
        ancestry: initialSection === 'ancestry',
        traits: initialSection === 'traits',
        equipment: initialSection === 'equipment',
        items: initialSection === 'items',
        domainCards: initialSection === 'domainCards',
        details: initialSection === 'details'
      };
    }
    // Default: expand campaign section
    return {
      campaign: true,
      class: false,
      ancestry: false,
      traits: false,
      equipment: false,
      items: false,
      domainCards: false,
      details: false
    };
  };

  const [expandedSections, setExpandedSections] = useState(getInitialExpandedSections());

  if (!allData) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">Failed to load game data. Please refresh.</p>
      </div>
    );
  }

  /**
   * Toggle section expansion
   * Only one section can be open at a time (accordion behavior)
   */
  function toggleSection(sectionKey) {
    setExpandedSections(prev => {
      const isCurrentlyExpanded = prev[sectionKey];

      if (isCurrentlyExpanded) {
        // Collapse this section
        return {
          ...prev,
          [sectionKey]: false
        };
      } else {
        // Collapse all others, expand this one
        return {
          campaign: false,
          class: false,
          ancestry: false,
          traits: false,
          equipment: false,
          items: false,
          domainCards: false,
          details: false,
          [sectionKey]: true
        };
      }
    });
  }

  /**
   * Check if a section is complete
   */
  function getSectionStatus(sectionKey) {
    switch (sectionKey) {
      case 'campaign':
        return character.campaignFrame ? 'complete' : 'incomplete';
      case 'class':
        return character.class && character.subclass ? 'complete' : 'incomplete';
      case 'ancestry':
        return character.ancestry?.length > 0 && character.community ? 'complete' : 'incomplete';
      case 'traits':
        const totalModifier = Object.values(character.traits || {}).reduce((sum, val) => sum + val, 0);
        return totalModifier !== 0 ? 'complete' : 'incomplete';
      case 'equipment':
        // Frames with equipment override (e.g., Motherboard Ikonis) skip standard equipment selection
        if (hasEquipmentOverride) {
          return 'complete';
        }
        // Check for armor and primary weapon (secondary weapon only needed if dual-wielding)
        const hasArmor = character.equipment?.armor;
        const hasPrimaryWeapon = character.equipment?.primaryWeapon;
        const hasWeaponChoice = character.equipment?.weaponChoice;
        const hasSecondaryIfNeeded =
          character.equipment?.weaponChoice === 'dual-wield'
            ? character.equipment?.secondaryWeapon
            : true; // Not dual-wielding, so secondary not needed
        return hasArmor && hasPrimaryWeapon && hasWeaponChoice && hasSecondaryIfNeeded ? 'complete' : 'incomplete';
      case 'items': {
        const hasPotion = !!character.startingItems?.potion;
        const classData = character.class ? allData?.classes?.[character.class] : null;
        const needsClassItem = classData?.classItems?.length === 2;
        const hasClassItem = !needsClassItem || !!character.startingItems?.classItem;
        return hasPotion && hasClassItem ? 'complete' : 'incomplete';
      }
      case 'domainCards':
        return character.loadout?.length >= 2 ? 'complete' : 'incomplete';
      case 'details':
        const hasName = character.name;
        const hasExperiences = character.experiences?.filter(e => e).length >= 2;
        const hasAppearance = character.appearanceText?.trim();
        return hasName && hasExperiences && hasAppearance ? 'complete' : 'incomplete';
      default:
        return 'incomplete';
    }
  }

  /**
   * Check if all sections are complete
   */
  function areAllSectionsComplete() {
    const sections = ['campaign', 'class', 'ancestry', 'traits', 'equipment', 'items', 'domainCards', 'details'];
    return sections.every(section => getSectionStatus(section) === 'complete');
  }

  /**
   * Complete Level 1 character creation
   * Delegates to parent-provided onCharacterForge function
   */
  function handleCompleteCharacter() {
    if (!areAllSectionsComplete()) {
      toast.error('Please complete all sections before forging your character');
      return;
    }

    // Delegate to parent's character forge handler
    onCharacterForge(character, allData);
  }

  return (
    <div className="max-w-4xl mx-auto space-y-3">
      <div className="mb-6">
        <h2 className="text-heading-lg text-red-400 font-serif mb-2">Create Your Character</h2>
        <p className="text-body text-gray-300">Complete each section to build your Level 1 character</p>
      </div>

      {/* Section 1: Campaign Frame */}
      <EditorSection
        title="Campaign Frame"
        number={1}
        status={getSectionStatus('campaign')}
        isExpanded={expandedSections.campaign}
        onToggle={() => toggleSection('campaign')}
      >
        <CampaignFrameSection character={character} onUpdate={onUpdate} allData={allData} />
      </EditorSection>

      {/* Section 2: Class & Subclass */}
      <EditorSection
        title="Class & Subclass"
        number={2}
        status={getSectionStatus('class')}
        isExpanded={expandedSections.class}
        onToggle={() => toggleSection('class')}
      >
        <ClassSection character={character} onUpdate={onUpdate} allData={allData} />
      </EditorSection>

      {/* Section 3: Ancestry & Community */}
      <EditorSection
        title="Ancestry & Community"
        number={3}
        status={getSectionStatus('ancestry')}
        isExpanded={expandedSections.ancestry}
        onToggle={() => toggleSection('ancestry')}
      >
        <AncestrySection character={character} onUpdate={onUpdate} allData={allData} />
      </EditorSection>

      {/* Section 4: Trait Assignment */}
      <EditorSection
        title="Trait Assignment"
        number={4}
        status={getSectionStatus('traits')}
        isExpanded={expandedSections.traits}
        onToggle={() => toggleSection('traits')}
      >
        <TraitsSection character={character} onUpdate={onUpdate} />
      </EditorSection>

      {/* Section 5: Equipment */}
      <EditorSection
        title="Equipment"
        number={5}
        status={getSectionStatus('equipment')}
        isExpanded={expandedSections.equipment}
        onToggle={() => toggleSection('equipment')}
      >
        <EquipmentSection character={character} onUpdate={onUpdate} allData={allData} />
      </EditorSection>

      {/* Section 6: Starting Items */}
      <EditorSection
        title="Starting Items"
        number={6}
        status={getSectionStatus('items')}
        isExpanded={expandedSections.items}
        onToggle={() => toggleSection('items')}
      >
        <StartingItemsSection character={character} onUpdate={onUpdate} allData={allData} />
      </EditorSection>

      {/* Section 7: Domain Cards */}
      <EditorSection
        title="Domain Cards"
        number={7}
        status={getSectionStatus('domainCards')}
        isExpanded={expandedSections.domainCards}
        onToggle={() => toggleSection('domainCards')}
      >
        <DomainCardsSection character={character} onUpdate={onUpdate} allData={allData} />
      </EditorSection>

      {/* Section 8: Character Details */}
      <EditorSection
        title="Character Details"
        number={8}
        status={getSectionStatus('details')}
        isExpanded={expandedSections.details}
        onToggle={() => toggleSection('details')}
      >
        <DetailsSection character={character} onUpdate={onUpdate} />
      </EditorSection>

      {/* Forge Character Button */}
      <div className="mt-8 p-6 bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-2xl">
        <div className="flex flex-col items-center gap-4">
          {areAllSectionsComplete() ? (
            <>
              <CheckCircle2 className="w-12 h-12 text-green-400" />
              <div className="text-center">
                <h3 className="text-heading-md text-white mb-2">Ready to Forge!</h3>
                <p className="text-gray-300 text-small mb-4">
                  All sections complete. Click below to forge your Level 1 character.
                </p>
              </div>
              <button
                onClick={handleCompleteCharacter}
                className="px-8 py-3 bg-primary hover:bg-primary/90 text-white font-bold rounded-lg transition-colors text-base"
              >
                Forge Character
              </button>
            </>
          ) : (
            <>
              <Circle className="w-12 h-12 text-gray-500" />
              <div className="text-center">
                <h3 className="text-heading-md text-white mb-2">Complete All Sections</h3>
                <p className="text-gray-300 text-small">
                  Finish all sections above to forge your character.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * EditorSection - Accordion section wrapper
 */
function EditorSection({ title, number, status, isExpanded, onToggle, children }) {
  const handleToggle = (e) => {
    e.preventDefault(); // Prevent any default behavior

    // Capture current scroll position
    const scrollY = window.scrollY;

    // Toggle the section
    onToggle();

    // Restore scroll position after React renders the expanded content
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    });
  };

  return (
    <div className={cn(
      "bg-gradient-to-br from-gray-900 to-black rounded-lg shadow-2xl border transition-all",
      status === 'complete' ? "border-green-500" : "border-red-700"
    )}>
      {/* Section header (clickable) */}
      <button
        onClick={handleToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-black/30 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-4">
          <div className={cn(
            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-small font-bold",
            status === 'complete' ? "bg-green-500 text-white" : "bg-surface-3 text-gray-400"
          )}>
            {number}
          </div>
          <div>
            <h3 className="text-heading-md text-red-400 font-serif">{title}</h3>
            {status === 'complete' && (
              <p className="text-small text-green-400">Complete</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {status === 'complete' ? (
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          ) : (
            <Circle className="w-5 h-5 text-gray-500" />
          )}
          <ChevronDown className={cn(
            "w-5 h-5 text-gray-400 transition-transform",
            isExpanded && "rotate-180"
          )} />
        </div>
      </button>

      {/* Section content (collapsible) */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2 overflow-hidden">
          {children}
        </div>
      )}
    </div>
  );
}

export default LevelOneEditor;
